import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import withMountGuard from 'hocs/withMountGuard'
import { useAppSelector } from 'hooks/reduxHooks'
import LoyaltyTierBenefitMessageBanner from 'loyalty/components/LoyaltyTierBenefitMessageBanner'
import LoyaltyStayUpgradeDetailsModal from 'loyalty/components/StayUpgrade/LoyaltyStayUpgradeDetailsModal'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import TextLink from 'LuxKit/TextLink'
import React, { useCallback, useContext } from 'react'
import OfferPageLoyaltyStayUpgradeCallOutBannerAction from './OfferPageLoyaltyStayUpgradeCallOutBannerAction'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'

interface Props {
  packageView: App.OfferPackageOptionView;
}

function OfferPageLoyaltyStayUpgradeCallOutBanner({ packageView }: Props) {
  const loyaltyUpgradeAvailability = packageView.loyaltyUpgradeAvailability
  const tier = useAppSelector(getLoyaltyDisplayTier)
  const showModal = useContext(ModalContext)
  const { offer, checkInDate, checkOutDate } = useContext(OfferPageStateContext)

  const showTargetUpgradeRoomDetails = useCallback(() => {
    switch (loyaltyUpgradeAvailability?.upgradeType) {
      case 'room-only':
        showModal(<LoyaltyStayUpgradeDetailsModal
          upgradeType="room"
          originPackage={packageView.package}
          targetPackage={loyaltyUpgradeAvailability.roomUpgradePackage}
          offer={offer}
          checkInDate={checkInDate}
          checkOutDate={checkOutDate}
        />)
        break
      case 'package-only':
        showModal(<LoyaltyStayUpgradeDetailsModal
          upgradeType="package"
          originPackage={packageView.package}
          targetPackage={loyaltyUpgradeAvailability.packageUpgradePackage}
          offer={offer}
          checkInDate={checkInDate}
          checkOutDate={checkOutDate}
        />)
        break
    }
  }, [checkInDate, checkOutDate, loyaltyUpgradeAvailability, offer, packageView, showModal])

  if (!loyaltyUpgradeAvailability) return null

  return <ProductPaletteProvider palette={`tier-${tier}`}>
    <LoyaltyTierBenefitMessageBanner
      tier={tier}
      benefitType="hotel_upgrade"
      endContent={<OfferPageLoyaltyStayUpgradeCallOutBannerAction packageView={packageView} />}
    >
      <Group direction="vertical" tabletDirection="horizontal" tabletGap="0 24" wrap="wrap">
        {loyaltyUpgradeAvailability.upgradeType === 'room-and-package' && <div>Free room or package upgrade</div>}
        {loyaltyUpgradeAvailability.upgradeType === 'room-only' && <>
          <CSSBreakpoint max="tablet">Apply free room upgrade</CSSBreakpoint>
          <CSSBreakpoint min="desktop">
            <Group direction="horizontal" gap={8}>
              <div>Apply free room upgrade to</div>
              <b>{loyaltyUpgradeAvailability.roomUpgradePackage.roomType?.name ?? loyaltyUpgradeAvailability.roomUpgradePackage.name}</b>
            </Group>
          </CSSBreakpoint>
          <TextLink size="caption" onClick={showTargetUpgradeRoomDetails}>View upgraded room</TextLink>
        </>}
        {loyaltyUpgradeAvailability.upgradeType === 'package-only' && <>
          <CSSBreakpoint max="tablet">Apply free package upgrade</CSSBreakpoint>
          <CSSBreakpoint min="desktop">
            <div>Apply free package upgrade</div>
          </CSSBreakpoint>
          <TextLink size="caption" onClick={showTargetUpgradeRoomDetails}>View upgraded inclusions</TextLink>
        </>}
      </Group>
    </LoyaltyTierBenefitMessageBanner>
  </ProductPaletteProvider>
}

export default withMountGuard(() => useAppSelector(getIsLoyaltyEnabled))(
  withOfferPageState((state) => ({
    offer: state.offer,
  }), OfferPageLoyaltyStayUpgradeCallOutBanner),
)
